import type { SortingRule } from 'react-table';
import type { ResizableDataField } from '@bringg/types';

import { ExceptionFilters, TranslatableOption } from '../../types/common.consts';

export type AdditionalFilters = Array<keyof ExceptionFilters>;
export interface TableConfigCommon {
	groupBy?: string[];
	filters?: ExceptionFilters;
	sortBy?: SortingRule<string>[];
}
export interface TableConfigFieldsData {
	orderedColumns?: TranslatableOption[];
	hiddenColumnIds?: string[];
	columnWidths?: Record<string, number>;
	additionalFiltersNames?: AdditionalFilters;
}
export interface TableConfig extends TableConfigCommon, TableConfigFieldsData {}
export interface TableConfigWithFields extends TableConfigCommon {
	fields?: ResizableDataField[];
}

function transformFieldsToTableFeed(fields: ResizableDataField[]): TableConfigFieldsData {
	return fields
		.slice()
		.sort((a, b) => a.position - b.position)
		.reduce<TableConfigFieldsData>(
			(acc, field) => {
				const { translate_key, active, id, width } = field;
				if (id) {
					if (translate_key) acc.orderedColumns.push({ id, translateKey: translate_key });
					if (!active) acc.hiddenColumnIds.push(id);
					if (width > 0) Object.assign(acc.columnWidths, { [id]: width });
				}
				return acc;
			},
			{ orderedColumns: [], hiddenColumnIds: [], columnWidths: {} }
		);
}

function transformTableFeedToFields({
	orderedColumns,
	hiddenColumnIds,
	columnWidths
}: TableConfigFieldsData): ResizableDataField[] {
	const hiddenColumnIdsSet = new Set(hiddenColumnIds);
	return orderedColumns.map<ResizableDataField>(({ id, translateKey }, index) => {
		const field: ResizableDataField = {
			id,
			translate_key: translateKey,
			position: index,
			active: !hiddenColumnIdsSet.has(id)
		};
		if (columnWidths[id]) {
			Object.assign<ResizableDataField, { width: number }>(field, { width: columnWidths[id] });
		}

		return field;
	});
}

export const getConfigWithFields = ({
	orderedColumns = [],
	hiddenColumnIds = [],
	columnWidths = {},
	...restConfig
}: TableConfig): TableConfigWithFields => {
	// Assumed that orderedColumns has all UI columns, otherwise this function will result in corrupted fields
	return {
		...restConfig,
		fields: transformTableFeedToFields({ orderedColumns, hiddenColumnIds, columnWidths })
	};
};

function ensureColumnsInFields(fields: ResizableDataField[], allColumns: TranslatableOption[]): ResizableDataField[] {
	// Do not need to ensure all columns in case of empty fields
	// default config will be applied in this case
	if (!fields?.length) {
		return fields;
	}

	// Ensure missing in config columns will be displayed in columns panel
	const fieldIdsSet = new Set(fields.map(({ id }) => id));
	const missingColumns: ResizableDataField[] = allColumns
		.filter(({ id }) => !fieldIdsSet.has(id))
		.map(({ id, translateKey }, index) => ({
			id,
			active: true,
			position: fields.length + index,
			translate_key: translateKey
		}));

	return fields.concat(missingColumns);
}

function isTableConfigWithFields(config: TableConfig | TableConfigWithFields): config is TableConfigWithFields {
	return Array.isArray((config as TableConfigWithFields).fields);
}

// TODO: remove legacy TableConfig branch after Q2_2022
export const getTaskAlertsTableConfig = (
	configOrigin: TableConfig | TableConfigWithFields,
	unsafeAdminFields: ResizableDataField[] = [],
	userLevelFieldsCustomizationAllowed = false,
	allColumns: TranslatableOption[]
): TableConfig => {
	// Be aware it is still a shallow copy
	const config = Object.assign<Record<string, number>, TableConfig | TableConfigWithFields>({}, configOrigin);
	const adminFields = Array.isArray(unsafeAdminFields) ? unsafeAdminFields : [];

	if (isTableConfigWithFields(config)) {
		const { fields, ...restConfig } = config;
		return Object.assign<TableConfigCommon, TableConfigFieldsData>(
			restConfig,
			transformFieldsToTableFeed(
				ensureColumnsInFields(
					userLevelFieldsCustomizationAllowed && fields?.length ? fields : adminFields,
					allColumns
				)
			)
		);
	}

	// TODO: remove check legacy after Q2_2022
	const { orderedColumns, hiddenColumnIds } = config;
	const isLegacyConfigApplied = [orderedColumns, hiddenColumnIds].some(value => value?.length);

	return isLegacyConfigApplied
		? config
		: Object.assign<TableConfig, TableConfigFieldsData>(
				config,
				transformFieldsToTableFeed(ensureColumnsInFields(adminFields, allColumns))
		  );
};
